import * as turf from '@turf/turf';

/**
 * @param {object} project with blocks and kml_layers
 * @param {number[]|null} selectedLayerIds selected kml_layer ids; null = all layers
 */
export function getVisibleBlocks(project, selectedLayerIds) {
  if (!project?.blocks?.length) return [];
  if (selectedLayerIds == null) {
    return project.blocks;
  }
  if (selectedLayerIds.length === 0) {
    return [];
  }
  const set = new Set(selectedLayerIds.map(Number));
  return project.blocks.filter((b) => set.has(Number(b.kml_layer_id)));
}

/** 1-based index of block among blocks of same kml_layer (by id asc). */
export function blockLayerNumber(block, blocksInSameContext) {
  const same = blocksInSameContext
    .filter((b) => Number(b.kml_layer_id) === Number(block.kml_layer_id))
    .sort((a, b) => a.id - b.id);
  const ix = same.findIndex((b) => b.id === block.id);
  return ix >= 0 ? ix + 1 : block.id;
}

export function parseStoredLayerIds(json) {
  try {
    const arr = JSON.parse(json);
    if (!Array.isArray(arr)) return null;
    return arr.map(Number).filter((n) => !Number.isNaN(n));
  } catch {
    return null;
  }
}

export function layerSelectionStorageKey(projectId) {
  return `selectedKmlLayerIds_${projectId}`;
}

const LAYER_PANEL_SCROLL_KEY = 'layerTreeScrollMaxPx';

export function getStoredLayerPanelScrollMaxPx(fallback = 260) {
  try {
    const n = parseInt(localStorage.getItem(LAYER_PANEL_SCROLL_KEY), 10);
    if (Number.isFinite(n) && n >= 100 && n <= 720) return n;
  } catch {
    /* ignore */
  }
  return fallback;
}

export function setStoredLayerPanelScrollMaxPx(px) {
  try {
    localStorage.setItem(LAYER_PANEL_SCROLL_KEY, String(px));
  } catch {
    /* ignore */
  }
}

/**
 * Leaflet fitBounds corners: [[south, west], [north, east]] (lat, lng)
 */
export function leafletBoundsFromBlocks(blocks) {
  const valid = (blocks || []).filter((b) => b?.block_geojson?.geometry);
  if (!valid.length) return null;
  try {
    const features = valid.map((b) => {
      const g = b.block_geojson;
      if (g && g.type === 'Feature') return g;
      return turf.feature(g.geometry || g);
    });
    const fc = turf.featureCollection(features);
    const [minLng, minLat, maxLng, maxLat] = turf.bbox(fc);
    if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) return null;
    if (
      Math.abs(maxLat - minLat) < 1e-12 ||
      Math.abs(maxLng - minLng) < 1e-12
    ) {
      return null;
    }
    return [
      [minLat, minLng],
      [maxLat, maxLng],
    ];
  } catch {
    return null;
  }
}

export function getBoundsForLayerIds(project, layerIds) {
  if (!project?.blocks?.length || !layerIds?.length) return null;
  const set = new Set(layerIds.map(Number));
  const subset = project.blocks.filter((b) =>
    set.has(Number(b.kml_layer_id))
  );
  return leafletBoundsFromBlocks(subset);
}
